import React, { FC, useRef, useState, Suspense } from 'react';
import { Box, theme, Text, useSideSheetState, IconSize, Popover } from '@bechtel/gds';
import {
  CardContainerStyled,
  CommodityGripIconStyled,
  CardHeaderStyled,
  IconCopyStyled,
  IconCopiedStyled,
  TextWithEllipsisStyled,
  StepContentStyled,
} from './styled';
import { find, toLower } from 'lodash';
import { MwrStatus } from '@bechtel/types';
import { CommodityCardProps } from '../types';
import { MilestoneDroppable } from './milestone';
import {
  SuspenseLoader,
  ErrorBoundary,
  EllipsisPlacement,
  ProgressStepBar,
  Step,
  useDate,
} from '@bechtel/client-common';
import { CommodityProgressStepBarProps, MaterialStatusTextProps, PlanningMaterialStatus } from './types';
import { Draggable } from 'react-beautiful-dnd';
import { useDragPermission } from '../hooks';
import { CommodityDetail } from '@bechtel/client-work-packages';
import { MWRHistoryPopperContent as MWRHistory } from './mwr-history';
import { t, Trans } from '@lingui/macro';

const getPlanningMaterialStatus = (material: CommodityProgressStepBarProps['material']): PlanningMaterialStatus => {
  if (!material) return PlanningMaterialStatus.NOT_APPLICABLE;
  if (material.mwrs && material.mwrs.length > 0 && find(material.mwrs, (mwr) => mwr.mwrStatus == MwrStatus.LOADED))
    return PlanningMaterialStatus.ISSUED;
  if (material?.mwrNumber) return PlanningMaterialStatus.MWR;
  if (material?.mrrNumber) return PlanningMaterialStatus.MRR;
  return PlanningMaterialStatus.NOT_APPLICABLE;
};

const getProgressStepColor = (planningMaterialStatus: PlanningMaterialStatus, step: number) => {
  if (planningMaterialStatus == PlanningMaterialStatus.NOT_APPLICABLE) return '';
  if (step == 1) {
    return planningMaterialStatus == PlanningMaterialStatus.MRR ||
      planningMaterialStatus == PlanningMaterialStatus.MWR ||
      planningMaterialStatus == PlanningMaterialStatus.ISSUED
      ? theme.colors.blue[700]
      : '';
  } else if (step == 2) {
    return planningMaterialStatus == PlanningMaterialStatus.MWR ||
      planningMaterialStatus == PlanningMaterialStatus.ISSUED
      ? theme.colors.blue[700]
      : '';
  } else {
    return planningMaterialStatus == PlanningMaterialStatus.ISSUED ? theme.colors.blue[700] : '';
  }
};

const CommodityProgressStepBar: FC<CommodityProgressStepBarProps> = ({ material }) => {
  const [show, setShow] = useState<boolean>(false);
  const refElement = useRef(null);
  const planningMaterialStatus = getPlanningMaterialStatus(material);
  const enableMWRHistory =
    planningMaterialStatus == PlanningMaterialStatus.MWR || planningMaterialStatus == PlanningMaterialStatus.ISSUED;
  return (
    <>
      <ProgressStepBar>
        <Step fillColor={getProgressStepColor(planningMaterialStatus, 1)}>
          <Text weight="600" size="200">
            <Trans>MRR</Trans>
          </Text>
        </Step>
        <Step
          fillColor={getProgressStepColor(planningMaterialStatus, 2)}
          enableHover={enableMWRHistory}
          hoverColor={theme.colors.blue[500]}
        >
          <StepContentStyled
            ref={refElement}
            // onClick={() => enableMWRHistory && setShow(!show)}
            onMouseEnter={() => enableMWRHistory && setShow(true)}
            onMouseLeave={() => enableMWRHistory && setShow(false)}
          >
            <Text weight="600" size="200">
              <Trans>MWR</Trans>
            </Text>
          </StepContentStyled>
        </Step>
        <Step fillColor={getProgressStepColor(planningMaterialStatus, 3)}>
          <Text weight="600" size="200">
            <Trans>Issued</Trans>
          </Text>
        </Step>
      </ProgressStepBar>
      {refElement && show && (
        <Popover triggerComponentRef={refElement} placement="bottom" showPopper={show} customStyle={{ zIndex: 1 }}>
          <ErrorBoundary>
            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
              <MWRHistory material={material} />
            </Suspense>
          </ErrorBoundary>
        </Popover>
      )}
    </>
  );
};

const MaterialStatusText: FC<MaterialStatusTextProps> = ({ material }) => {
  const { formatISODate } = useDate();
  const planningMaterialStatus = getPlanningMaterialStatus(material);
  const mwr =
    planningMaterialStatus == PlanningMaterialStatus.ISSUED
      ? find(material?.mwrs, (mwr) => mwr.mwrStatus == MwrStatus.LOADED)
      : material?.mwrs && material?.mwrs.length > 0 && material?.mwrs[0];
  return (
    <Box mt={{ sm: '100' }} mb={{ sm: '200' }} pb={{ sm: '200' }} pt={{ sm: '400' }}>
      {(() => {
        switch (planningMaterialStatus) {
          case PlanningMaterialStatus.MRR:
            return (
              <Text size="200" weight="600" color="grey.600" family="graphik" data-testid="commodity-date">
                <Trans>MRR</Trans> &nbsp; {material?.mrrNumber ? `${material?.mrrNumber}` : ''}
              </Text>
            );
          case PlanningMaterialStatus.MWR:
            return (
              <Text size="200" weight="600" color="grey.600" family="graphik" data-testid="commodity-date">
                <Trans>MWR</Trans> &nbsp; {material?.mwrNumber ? `${material?.mwrNumber}` : ''}
              </Text>
            );
          case PlanningMaterialStatus.ISSUED:
            return (
              <Text size="200" weight="600" color="grey.600" family="graphik" data-testid="commodity-date">
                <Trans>Issued</Trans> &nbsp; {material && mwr && mwr?.mwrDate ? `(${formatISODate(mwr.mwrDate)})` : ''}
              </Text>
            );
          default:
            return (
              <Text
                style={{ whiteSpace: 'nowrap' }}
                size="200"
                weight="600"
                color="grey.600"
                family="graphik"
                data-testid="commodity-date"
              >
                <Trans>No Material Status Found</Trans>
              </Text>
            );
        }
      })()}
    </Box>
  );
};

export const CommodityCard: FC<CommodityCardProps> = ({
  commodityMilestones,
  commodityId,
  droppableId,
  draggableId,
  dragIndex,
  setSelectedMilestoneDraggableIds,
  selectedMilestoneDraggableIds,
  draggingMilestoneId,
  milestoneIdentifiers,
}) => {
  const commodity = commodityMilestones[0].commodity?.materialCommodity || commodityMilestones[0].commodity?.weld;
  //TODO- identify way to find commodity type.string comparison is the only way as of now.
  const isWeldCommodity = toLower(commodity?.type.name) === 'weld';
  const materialItem = isWeldCommodity ? undefined : commodityMilestones[0].commodity?.materialCommodity?.material;
  const {
    visible: visibleCommodityDetail,
    open: openCommodityDetailSideSheet,
    close: closeCommodityDetailSideSheet,
  } = useSideSheetState(false);
  const canPlanCommodity = useDragPermission(commodityMilestones);
  const commodityTagHiddenRef = useRef<HTMLInputElement>(null);
  const [showCopied, setShowCopied] = useState(false);
  // const [showToCopy, setShowToCopy] = useState(false);
  return (
    <Draggable key={draggableId} draggableId={draggableId} index={dragIndex} isDragDisabled={!canPlanCommodity}>
      {(provided, snapshot) => {
        return (
          <CardContainerStyled
            data-testid={`commditycard`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...snapshot}
          >
            <CardHeaderStyled canPlan={canPlanCommodity} data-testid={`commodity-type`} {...provided.dragHandleProps}>
              <input type="hidden" ref={commodityTagHiddenRef} defaultValue={commodity?.tag}></input>
              <TextWithEllipsisStyled
                size="300"
                title={commodity?.tag}
                color="blue.700"
                weight="600"
                ellipsisPlacement={EllipsisPlacement.BEGINNING}
              >
                {commodity?.tag}
                {!showCopied ? (
                  <IconCopyStyled
                    size={IconSize.SMALL}
                    icon="copy"
                    fillColor="blue.800"
                    title={t`Click to copy commodity tag`}
                    onClick={async () => {
                      if (!commodityTagHiddenRef || !commodityTagHiddenRef.current) return false;
                      commodityTagHiddenRef.current.focus();
                      commodityTagHiddenRef.current.select();
                      if (navigator.clipboard) await navigator.clipboard.writeText(commodityTagHiddenRef.current.value);
                      else document.execCommand('copy');
                      setShowCopied(true);
                      setTimeout(() => {
                        setShowCopied(false);
                      }, 800);
                    }}
                  />
                ) : (
                  <IconCopiedStyled icon="check-circle" title={t`Copied`} fillColor="blue.1000" />
                )}
              </TextWithEllipsisStyled>
              <CommodityGripIconStyled
                icon={canPlanCommodity ? 'grip-vertical' : 'lock'}
                data-testid={canPlanCommodity ? 'drag-handler' : 'no-drag-handler'}
              />
            </CardHeaderStyled>
            {commodity && materialItem && (
              <>
                <MaterialStatusText material={materialItem} />
                <CommodityProgressStepBar material={materialItem} />
              </>
            )}
            <MilestoneDroppable
              draggableId={draggableId}
              droppableId={droppableId}
              commodityId={commodityId}
              commodityMilestones={commodityMilestones}
              setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
              selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
              draggingMilestoneId={draggingMilestoneId}
              milestoneIdentifiers={milestoneIdentifiers}
            ></MilestoneDroppable>
            {visibleCommodityDetail && commodity && (
              <ErrorBoundary>
                <Suspense fallback={<SuspenseLoader />}>
                  <CommodityDetail
                    commodityId={commodity.id}
                    commodityName={commodity.type.name}
                    commodityTag={commodity.tag}
                    workPackageTag={commodity.workPackage.tag}
                    close={closeCommodityDetailSideSheet}
                    showDocuments={true}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
            <Box justifyContent={{ sm: 'flex-end' }} my={{ sm: '300' }}>
              <Text
                size="200"
                color="grey.500"
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={openCommodityDetailSideSheet}
              >
                <Trans>View Details</Trans>
              </Text>
            </Box>
          </CardContainerStyled>
        );
      }}
    </Draggable>
  );
};
